/** Wire7 device facts contain only profile/model/options; Host owns the complete system prompt. */
import { z } from "zod";
import { InputPreparationModelSchema, ProviderProfileBindingSchema } from "@byok-sdk/protocol";

export const PrivateAgentPreparationFactsSchemaVersion =
  "salesko.private_agent_preparation_facts.v2" as const;

/** The audience a device assertion must carry to report preparation facts. */
export const PrivateAgentPreparationFactsAudience = "salesko.private-agent-preparation-facts.v2" as const;

/**
 * The option fields a DEVICE determines. `temperature` and `toolChoice` are absent by
 * rule: a real launch emits them only when a caller states them, and no caller does.
 */
export const PrivateAgentPreparationFactOptionsSchema = z
  .object({
    cacheRetention: z.enum(["none", "short", "long"]),
    maxTokens: z.number().int().positive(),
    /** The Pi thinking LEVEL, never the wire effort: the level→wire mapping happens inside pi-ai. */
    reasoningEffort: z.enum(["minimal", "low", "medium", "high", "xhigh", "max"]).optional()
  })
  .strict();
export type PrivateAgentPreparationFactOptions = z.infer<typeof PrivateAgentPreparationFactOptionsSchema>;

/** What the device states, exactly. The provider-profile triple is how it is keyed. */
export const PrivateAgentPreparationFactsSchema = z
  .object({
    profileRef: ProviderProfileBindingSchema.shape.profileRef,
    profileRevision: ProviderProfileBindingSchema.shape.profileRevision,
    profileHash: ProviderProfileBindingSchema.shape.profileHash,
    selection: z.object({ model: InputPreparationModelSchema, options: PrivateAgentPreparationFactOptionsSchema }).strict()
  })
  .strict();
export type PrivateAgentPreparationFacts = z.infer<typeof PrivateAgentPreparationFactsSchema>;

/**
 * The stored record: the reported facts plus the device they came from. `deviceId` is a
 * Host-added field — it is taken from the authenticated assertion, never from the body —
 * and it is part of the record because facts are only valid for the device that resolved
 * them.
 */
export const PrivateAgentPreparationFactsRecordSchema = PrivateAgentPreparationFactsSchema
  .extend({ deviceId: z.string().min(1).max(200) })
  .strict();
export type PrivateAgentPreparationFactsRecord = z.infer<typeof PrivateAgentPreparationFactsRecordSchema>;

/** What the DEVICE posts to control: an opaque device assertion plus the reported facts. */
export const PrivateAgentPreparationFactsTriggerSchema = z
  .object({ assertion: z.unknown(), agentId: z.string().min(1).max(200), facts: PrivateAgentPreparationFactsSchema })
  .strict();
export type PrivateAgentPreparationFactsTrigger = z.infer<typeof PrivateAgentPreparationFactsTriggerSchema>;

/** What control asks the API: the facts, plus the principal from the authenticated device. */
export const PrivateAgentPreparationFactsPrincipalSchema = PrivateAgentPreparationFactsTriggerSchema
  .omit({ assertion: true })
  .extend({ tenantRef: z.string().min(1).max(200), deviceId: z.string().min(1).max(200) })
  .strict();
export type PrivateAgentPreparationFactsPrincipal = z.infer<typeof PrivateAgentPreparationFactsPrincipalSchema>;

/** One acknowledgement. The record itself is not echoed back on this wire. */
export const PrivateAgentPreparationFactsResponseSchema = z
  .object({ schemaVersion: z.literal(PrivateAgentPreparationFactsSchemaVersion), accepted: z.literal(true) })
  .strict();
export type PrivateAgentPreparationFactsResponse = z.infer<typeof PrivateAgentPreparationFactsResponseSchema>;
